// When the vehicle actually runs for a booking or quote. The travel date is the package's Day 1;
// an extra pre-night that includes the vehicle starts it earlier and an extra post-night that
// includes the vehicle ends it later. Stay-only nights leave both on the package dates, since the
// customer reaches that hotel on their own. Mirrors BookingDurations on the backend.
//
// `quote` can be a saved quote snapshot (extension under `tripExtension`) or the lead form's quote
// request (extension fields at the top level).

export function vehicleTripDates(travelDate, quote, baseNightsFallback) {
  const preDays = vehicleExtensionDays(quote, "PRE");
  const postDays = vehicleExtensionDays(quote, "POST");
  const baseNights = Number(quote?.baseDurationNights ?? baseNightsFallback);
  const date = /^\d{4}-\d{2}-\d{2}/.test(String(travelDate || "")) ? String(travelDate).slice(0, 10) : "";
  return {
    preDays,
    postDays,
    start: date ? addDays(date, -preDays) : "",
    end: date && Number.isFinite(baseNights) && baseNights >= 0 ? addDays(date, baseNights + postDays) : "",
  };
}

// The whole trip, stay-only extra nights included: from the first extra pre-night to the morning
// after the last extra post-night. Customer documents (receipt, travel policy) show this span; the
// vehicle dates above stay on the days the vehicle actually runs.
export function tripStayDates(travelDate, quote, baseNightsFallback) {
  const preNights = extraNights(quote, "PRE");
  const postNights = extraNights(quote, "POST");
  const baseNights = Number(quote?.baseDurationNights ?? baseNightsFallback);
  const date = /^\d{4}-\d{2}-\d{2}/.test(String(travelDate || "")) ? String(travelDate).slice(0, 10) : "";
  return {
    start: date ? addDays(date, -preNights) : "",
    end: date && Number.isFinite(baseNights) && baseNights >= 0 ? addDays(date, baseNights + postNights) : "",
  };
}

function extraNights(quote, position) {
  if (!quote) return 0;
  const extension = quote.tripExtension && typeof quote.tripExtension === "object" ? quote.tripExtension : null;
  const required = extension && "required" in extension ? extension.required : quote.tripExtensionRequired;
  if (required === false) return 0;
  return Math.max(0, Number(position === "PRE" ? quote.extraPreNights : quote.extraPostNights) || 0);
}

export function vehicleExtensionDays(quote, position) {
  const nights = extraNights(quote, position);
  if (!nights) return 0;
  const extension = quote.tripExtension && typeof quote.tripExtension === "object" ? quote.tripExtension : null;
  const quoteScope = quote.extensionScope || "BOTH";
  const stays = [extension?.extensionStays, quote.quoteRequest?.extensionStays, quote.extensionStays]
    .find((list) => Array.isArray(list) && list.length);
  // Quotes saved before each night had its own scope: one scope covered every extra night.
  if (!stays) return includesVehicle(quoteScope) ? nights : 0;
  return stays.reduce((furthest, stay) => {
    const stayPosition = String(stay?.position || "PRE").toUpperCase() === "POST" ? "POST" : "PRE";
    if (stayPosition !== position || !includesVehicle(stay?.scope || quoteScope)) return furthest;
    const nightIndex = Math.max(1, Number(stay?.nightIndex) || 1);
    return nightIndex <= nights ? Math.max(furthest, nightIndex) : furthest;
  }, 0);
}

function includesVehicle(scope) {
  const normalized = String(scope || "BOTH").trim().toUpperCase();
  return normalized === "VEHICLE" || normalized === "BOTH";
}

function addDays(isoDate, days) {
  const [year, month, day] = isoDate.split("-").map(Number);
  const date = new Date(Date.UTC(year, month - 1, day + days));
  return date.toISOString().slice(0, 10);
}
